// relationSlotKind — "is the identifier token at [start,end) sitting in a slot
// that only ever holds a relation reference?"
//
// EXTRACTED (not rewritten) from `edit/applyStructuralEdit.cte.ts`, where it
// was the module-private layer-2 gate of SED-7's scope-aware CTE-rename filter.
// The navigational rename feature needs the exact same test for buffer-local
// alias/CTE rename and for rewriting open query tabs after a DB-object rename,
// so the primitive moved here and the CTE planner imports it back. Default
// behaviour is byte-identical to the original — the DML introducer set and the
// FROM-list comma disambiguation are opt-in via `RelationSlotOptions`.
//
// Returns:
//   'dotted-head' — the candidate is the qualifier head of `x.col`. NOT
//                   relation-exclusive: `x` can be a FROM/JOIN table ALIAS that
//                   shadows a same-named relation/CTE, so callers must add
//                   their own alias-shadow guard on this arm.
//   'bare'        — the candidate is the first significant token after a
//                   relation introducer (FROM / JOIN / APPLY / LATERAL, a
//                   FROM-list comma, or an opening paren). An alias
//                   *declaration*, select-list alias, column, or parameter is
//                   never in this position at any nesting depth.
//   'none'        — anything else.
//
// Whitespace and line/block comments between the introducer and the candidate
// are skipped. The test is a purely local property of the candidate's
// neighbourhood, so it holds identically at every scope depth.

const IDENT_CONT = /[A-Za-z0-9_$]/;

export type RelationSlotKind = 'dotted-head' | 'bare' | 'none';

/** Keywords whose immediately-following identifier is a relation reference.
 *  APPLY/LATERAL cover MSSQL CROSS/OUTER APPLY and PG LATERAL derived sources;
 *  the candidate after them is still a relation. UNION/INTERSECT/EXCEPT are not
 *  listed — a set-op branch starts with SELECT, never a bare relation token. */
export const RELATION_INTRODUCERS: ReadonlySet<string> = new Set<string>([
  'FROM', 'JOIN', 'APPLY', 'LATERAL',
]);

/** `RELATION_INTRODUCERS` widened with the DML statement heads whose next token
 *  is also a relation (`INSERT INTO t`, `UPDATE t SET`, `MERGE INTO t`,
 *  `… USING t`, MySQL `STRAIGHT_JOIN t`, `TRUNCATE TABLE t`). Used by the
 *  navigational tab-rewrite planner, which must find relation references in
 *  whole user buffers, not just SELECT bodies. */
export const RELATION_INTRODUCERS_WITH_DML: ReadonlySet<string> = new Set<string>([
  ...RELATION_INTRODUCERS,
  'INTO', 'UPDATE', 'USING', 'STRAIGHT_JOIN', 'TABLE',
]);

export type RelationSlotOptions = {
  /** Defaults to `RELATION_INTRODUCERS` (the SED-7 set). */
  readonly introducers?: ReadonlySet<string>;
  /** When true, a preceding `,` is only accepted as a FROM-list separator if
   *  walking further left reaches a relation introducer before any other
   *  keyword. This rejects `SELECT 1, orders FROM t` (a select-list comma)
   *  while keeping `FROM a, b, c`. Defaults to false — the SED-7 behaviour,
   *  which accepts every comma. */
  readonly disambiguateComma?: boolean;
};

export function relationSlotKind(
  source: string,
  start: number,
  end: number,
  options?: RelationSlotOptions,
): RelationSlotKind {
  // Dotted qualifier head: `cte.col` — the byte right after the bare token is
  // a `.` (callers' lexical scanners already exclude `before === '.'`).
  if (source[end] === '.') return 'dotted-head';
  const introducers = options?.introducers ?? RELATION_INTRODUCERS;
  const i = walkLeftToToken(source, start - 1);
  if (i < 0) return 'none';
  const prev = source[i];
  // Opening paren (derived table, set-op branch, `IN (`).
  if (prev === '(') return 'bare';
  if (prev === ',') {
    if (!options?.disambiguateComma) return 'bare';
    return commaIsFromListSeparator(source, i, introducers) ? 'bare' : 'none';
  }
  return keywordAt(source, i, introducers) ? 'bare' : 'none';
}

/** True when the word ending at `i` (inclusive) is one of `introducers`. */
function keywordAt(source: string, i: number, introducers: ReadonlySet<string>): boolean {
  if (!IDENT_CONT.test(source[i])) return false;
  let s = i;
  while (s >= 0 && IDENT_CONT.test(source[s])) s--;
  return introducers.has(source.slice(s + 1, i + 1).toUpperCase());
}

/** Walk left from a FROM-list candidate's comma over the sibling entries to the
 *  governing keyword. `FROM a, b` reaches FROM (accept); `SELECT 1, orders FROM t`
 *  reaches SELECT (reject). A `)` on the way (`FROM (…) x, y`) is treated as an
 *  accept — resolving it would need full paren matching and the conservative
 *  answer keeps a genuine FROM-list entry renamable. */
function commaIsFromListSeparator(
  source: string,
  commaIdx: number,
  introducers: ReadonlySet<string>,
): boolean {
  let i = walkLeftToToken(source, commaIdx - 1);
  for (let guard = 0; guard < 64 && i >= 0; guard += 1) {
    const c = source[i];
    if (c === ')') return true;
    if (c === '(') return true;
    if (c === ',') { i = walkLeftToToken(source, i - 1); continue; }
    if (!IDENT_CONT.test(c)) { i = walkLeftToToken(source, i - 1); continue; }
    let s = i;
    while (s >= 0 && IDENT_CONT.test(source[s])) s--;
    const word = source.slice(s + 1, i + 1).toUpperCase();
    if (introducers.has(word)) return true;
    if (SLOT_TERMINATORS.has(word)) return false;
    i = walkLeftToToken(source, s);
  }
  return false;
}

/** Clause keywords that prove the comma we walked past was NOT a FROM-list
 *  separator (a select list, a VALUES row, an ORDER BY list, …). */
const SLOT_TERMINATORS: ReadonlySet<string> = new Set<string>([
  'SELECT', 'WHERE', 'GROUP', 'ORDER', 'HAVING', 'VALUES', 'SET', 'ON',
  'RETURNING', 'WINDOW', 'PARTITION', 'BY', 'WITH', 'DISTINCT',
]);

/** Walk left over whitespace / line + block comments to the preceding
 *  significant byte. Returns -1 when the range is exhausted. */
function walkLeftToToken(source: string, from: number): number {
  let i = from;
  for (;;) {
    while (i >= 0 && /\s/.test(source[i])) i--;
    if (i >= 1 && source[i] === '/' && source[i - 1] === '*') {
      // skip back over a `/* ... */` block comment
      i -= 2;
      while (i >= 1 && !(source[i] === '*' && source[i - 1] === '/')) i--;
      i -= 2;
      continue;
    }
    // Skip back over a `-- …` / `# …` line comment: such a comment always ends
    // at the newline we just consumed as whitespace, so the current line's
    // earliest `--`/`#` marker (left of i) starts a comment that contains i.
    // Conservative — a marker inside a string on that line over-skips, which
    // only ever yields a safe false-negative.
    if (i >= 0) {
      const lineStart = source.lastIndexOf('\n', i) + 1;
      let m = -1;
      for (let k = lineStart; k < i; k++) {
        if (source[k] === '#' || (source[k] === '-' && source[k + 1] === '-')) { m = k; break; }
      }
      if (m >= 0) { i = m - 1; continue; }
    }
    return i;
  }
}
